import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { runningPath, runningUrl, runningCompleteProfilePath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';
import { formatScheduleDateTime } from '../utils/scheduleTimeZone';
import './RunningPracticeDetail.css';

const STATUS_OPTIONS = [
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'MAYBE', label: 'Maybe' },
  { value: 'RESERVE', label: 'Reserve' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
];

const RunningPracticeDetail = () => {
  const { id } = useParams();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [practice, setPractice] = useState(null);
  const [votes, setVotes] = useState([]);
  const [player, setPlayer] = useState(null);
  const [myStatus, setMyStatus] = useState('AVAILABLE');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const practiceData = await api.getRunningPractice(id);
      setPractice(practiceData);
      if (practiceData.shareUrl) {
        setShareUrl(practiceData.shareUrl);
      } else if (practiceData.shareToken) {
        setShareUrl(runningUrl(`/public/practices/${practiceData.shareToken}`));
      }

      let clubAdmin = false;
      try {
        const club = await api.getClubById(practiceData.clubId);
        clubAdmin = !!(user?.id && (club.adminUserId === user.id || isAdmin()));
        setCanManage(clubAdmin);
      } catch {
        setCanManage(false);
      }

      const availability = await api.getRunningPracticeAvailability(id, user?.id);
      setVotes(Array.isArray(availability) ? availability : []);

      if (isAuthenticated && user?.id) {
        let membershipPlayerId = null;
        try {
          const runner = await api.getMyRunnerProfile(user.id);
          membershipPlayerId = runner?.membershipPlayerId;
          if (membershipPlayerId) {
            setPlayer({ id: membershipPlayerId, name: runner?.name || user?.name });
          }
        } catch {
          // No running profile yet
        }

        if (!membershipPlayerId) {
          const players = await api.getAllPlayers(user.id);
          const me = (players || []).find((p) => p.userId === user.id) || null;
          setPlayer(me);
          membershipPlayerId = me?.id;
        }

        if (membershipPlayerId) {
          const mine = (availability || []).find((v) => v.playerId === membershipPlayerId);
          if (mine) {
            setMyStatus(mine.status);
            setNotes(mine.notes || '');
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load practice');
    } finally {
      setLoading(false);
    }
  }, [id, user, isAuthenticated, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVote = async (e) => {
    e.preventDefault();
    if (!player?.id) {
      setError('Complete your athlete profile to vote on availability.');
      return;
    }
    try {
      setVoting(true);
      setError('');
      setSuccess('');
      await api.voteRunningPracticeAvailability(
        id,
        player.id,
        { status: myStatus, notes: notes.trim() || null },
        user.id
      );
      setSuccess('Availability saved');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save vote');
    } finally {
      setVoting(false);
    }
  };

  const handleShare = async () => {
    try {
      setError('');
      const result = await api.shareRunningPractice(id, user.id);
      const url = result.shareUrl || runningUrl(`/public/practices/${result.shareToken}`);
      setShareUrl(url);
      setPractice((prev) => (prev ? { ...prev, shareToken: result.shareToken, shareUrl: url } : prev));
      setSuccess('Share link ready');
    } catch (err) {
      setError(err.message || 'Failed to create share link');
    }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccess('Link copied');
    } catch {
      setError('Could not copy link');
    }
  };

  const handlePhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      await api.uploadRunningPracticePhotos(id, files, user.id);
      setSuccess('Photos uploaded');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    try {
      await api.deleteRunningPracticePhoto(id, photoUrl, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete photo');
    }
  };

  if (loading) return <div className="loading">Loading practice...</div>;
  if (!practice) return <div className="error-message">{error || 'Practice not found'}</div>;

  const counts = votes.reduce(
    (acc, vote) => {
      acc[vote.status] = (acc[vote.status] || 0) + 1;
      return acc;
    },
    { AVAILABLE: 0, MAYBE: 0, RESERVE: 0, UNAVAILABLE: 0 }
  );

  const whatsappHref = shareUrl
    ? api.shareRunningPracticeViaWhatsApp({
        title: practice.title,
        practiceDate: practice.practiceDate,
        gatheringLocation: practice.gatheringLocation,
        routeTitle: practice.route?.title,
        shareUrl,
      })
    : null;

  return (
    <div className="running-practice-detail">
      <p className="running-practice-detail__eyebrow">
        <Link to={runningPath(`/clubs/${practice.clubId}`)}>Back to club</Link>
      </p>
      <header className="running-practice-detail__header">
        <h1>{practice.title}</h1>
        <p className="running-practice-detail__when">{formatScheduleDateTime(practice.practiceDate)}</p>
        <p className="running-practice-detail__meet">
          Gathering: <strong>{practice.gatheringLocation}</strong>
        </p>
        {practice.gatheringNotes && (
          <p className="running-practice-detail__notes">{practice.gatheringNotes}</p>
        )}
        {practice.description && <p>{practice.description}</p>}
        {practice.status !== 'SCHEDULED' && (
          <p className="running-practice-detail__status">Status: {practice.status}</p>
        )}
      </header>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {practice.route && (
        <section className="running-practice-detail__section">
          <h2>Selected route</h2>
          <div className="running-practice-detail__route">
            <h3>{practice.route.title}</h3>
            {practice.route.distanceKm != null && <p>{practice.route.distanceKm} km</p>}
            <p>
              {[practice.route.surface, practice.route.difficulty].filter(Boolean).join(' · ')}
            </p>
            {practice.route.startLocation && <p>Start: {practice.route.startLocation}</p>}
            {practice.route.mapUrl && (
              <a href={practice.route.mapUrl} target="_blank" rel="noreferrer">
                Open map
              </a>
            )}
            <PhotoGallery photos={practice.route.photoUrls || []} />
          </div>
        </section>
      )}

      <section className="running-practice-detail__section">
        <h2>Availability</h2>
        <div className="running-practice-detail__counts">
          <span>Available {counts.AVAILABLE}</span>
          <span>Maybe {counts.MAYBE}</span>
          <span>Reserve {counts.RESERVE}</span>
          <span>Unavailable {counts.UNAVAILABLE}</span>
          {practice.runnerCapacity != null && (
            <span>Capacity {practice.runnerCapacity}</span>
          )}
        </div>

        {isAuthenticated ? (
          <form className="running-practice-detail__vote" onSubmit={handleVote}>
            <div className="running-practice-detail__status-grid">
              {STATUS_OPTIONS.map((opt) => (
                <label key={opt.value} className={myStatus === opt.value ? 'is-active' : ''}>
                  <input
                    type="radio"
                    name="status"
                    value={opt.value}
                    checked={myStatus === opt.value}
                    onChange={() => setMyStatus(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <label>
              Notes
              <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200} />
            </label>
            {!player && (
              <p className="running-practice-detail__hint">
                <Link to={runningCompleteProfilePath()}>Complete your running profile</Link> to vote.
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={voting || !player}>
              {voting ? 'Saving...' : 'Save availability'}
            </button>
          </form>
        ) : (
          <p>
            <Link to="/login">Log in</Link> to vote on this practice.
          </p>
        )}

        <ul className="running-practice-detail__votes">
          {votes.map((vote) => (
            <li key={vote.id}>
              <strong>{vote.player?.name || 'Runner'}</strong>
              <span>{vote.status}</span>
              {vote.notes && <em>{vote.notes}</em>}
            </li>
          ))}
        </ul>
      </section>

      <section className="running-practice-detail__section">
        <h2>Share with the team</h2>
        <p>Practice details, route, gathering spot, and photo gallery are included.</p>
        <div className="running-practice-detail__share-actions">
          {canManage && (
            <button type="button" className="btn btn-primary" onClick={handleShare}>
              {shareUrl ? 'Refresh share link' : 'Create share link'}
            </button>
          )}
          {shareUrl && (
            <>
              <button type="button" className="btn btn-secondary" onClick={copyShare}>
                Copy link
              </button>
              {whatsappHref && (
                <a className="btn btn-secondary" href={whatsappHref} target="_blank" rel="noreferrer">
                  Share on WhatsApp
                </a>
              )}
            </>
          )}
        </div>
        {shareUrl && (
          <p className="running-practice-detail__share-url">
            <a href={shareUrl}>{shareUrl}</a>
          </p>
        )}
      </section>

      <section className="running-practice-detail__section">
        <div className="running-practice-detail__photos-head">
          <h2>Practice photos</h2>
          {isAuthenticated && (
            <label className="btn btn-secondary running-practice-detail__file">
              Upload photos
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => handlePhotos(e.target.files)}
              />
            </label>
          )}
        </div>
        <PhotoGallery photos={practice.photoUrls || []} />
        {canManage && (practice.photoUrls || []).length > 0 && (
          <ul className="running-practice-detail__photo-admin">
            {practice.photoUrls.map((url) => (
              <li key={url}>
                <button type="button" className="btn btn-secondary" onClick={() => handleDeletePhoto(url)}>
                  Remove photo
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default RunningPracticeDetail;
